import type { ApiResponse } from './generic';
import {
  AcknowledgementStatus,
  type BeforeEmployeeStep,
  type DocumentLanguage,
  type DocumentSalaryInput,
  type MyAcknowledgement,
  type RecipientAnswers,
  type ProfileUpdateView,
  type RecipientAnswerView,
  type RecipientFieldView,
} from './esignature';

/**
 * One-to-one signing requests (docs/product/e-signatures-documenso.md 4.2 to
 * 6.5). Mirrors the backend `esignature` module's requests. Signing URLs,
 * gate tokens and codes never travel in these shapes.
 */

export enum SignatureRequestStatus {
  Draft = 'draft',
  PendingApproval = 'pending_approval',
  Sent = 'sent',
  Viewed = 'viewed',
  Signed = 'signed',
  Declined = 'declined',
  Expired = 'expired',
  Cancelled = 'cancelled',
  Failed = 'failed',
}

export enum SignatureSignerStatus {
  Waiting = 'waiting',
  Sent = 'sent',
  Viewed = 'viewed',
  Signed = 'signed',
  Declined = 'declined',
  Expired = 'expired',
}

export enum SignatureSignerRole {
  Recipient = 'recipient',
  CompanySignatory = 'company_signatory',
}

/** Who a request is waiting on, for the list and Needs attention. */
export type SignatureWaitingOn = 'approval' | 'company' | 'recipient' | null;

/** Error codes of signing requests (`errorCode` in an error response). */
export enum SignatureRequestErrorCode {
  EsignatureDisabled = 'ESIGNATURE_DISABLED',
  TemplateNotForSignature = 'TEMPLATE_NOT_FOR_SIGNATURE',
  TemplateInactive = 'TEMPLATE_INACTIVE',
  RecipientNoEmail = 'RECIPIENT_NO_EMAIL',
  SignatoryRequired = 'SIGNATORY_REQUIRED',
  SignatoryInvalid = 'SIGNATORY_INVALID',
  SameSignerEmail = 'SAME_SIGNER_EMAIL',
  ApproverRequired = 'APPROVER_REQUIRED',
  ApproverInvalid = 'APPROVER_INVALID',
  SelfApproval = 'SELF_APPROVAL',
  ApprovalNotSkippable = 'APPROVAL_NOT_SKIPPABLE',
  CommentRequired = 'COMMENT_REQUIRED',
  ReasonRequired = 'REASON_REQUIRED',
  ReminderTooSoon = 'REMINDER_TOO_SOON',
  ReminderNotSent = 'REMINDER_NOT_SENT',
  NotYourTurn = 'NOT_YOUR_TURN',
  ProviderFailed = 'PROVIDER_FAILED',
  SendAgainUnavailable = 'SEND_AGAIN_UNAVAILABLE',
  FileUnavailable = 'FILE_UNAVAILABLE',
  InvalidStatus = 'INVALID_STATUS',
  StartDateRequired = 'START_DATE_REQUIRED',
  ApproverManagerMissing = 'APPROVER_MANAGER_MISSING',
  ApproverIsRecipient = 'APPROVER_IS_RECIPIENT',
  NotTheApprover = 'NOT_THE_APPROVER',
  SignFirstWithAnswers = 'SIGN_FIRST_WITH_ANSWERS',
}

export type SignatureFileKind =
  'source' | 'signed' | 'certificate' | 'audit-log' | 'evidence';

export type SignatureComposeAction = 'send' | 'request_approval';

/** What HR sends to compose (and send, or ask approval for) one request. */
export interface SignatureRequestPayload {
  /** A template, or an uploaded PDF (`uploadedPdfKey` + `title`), phase 4. */
  templateId?: string;
  uploadedPdfKey?: string;
  /** The uploaded PDF's file name as picked, shown when it is revised. */
  uploadedPdfName?: string;
  title?: string;
  /** The employee who signs; or `applicationId` for a candidate (offer). */
  employeeId?: string;
  /** ATS offer: the candidate's application (needs recruiting.update). */
  applicationId?: string;
  /** ATS offer: the start date offered (YYYY-MM-DD), required. */
  startDate?: string;
  /** An uploaded PDF's language. A template's document uses its own. */
  language?: DocumentLanguage;
  salary?: DocumentSalaryInput;
  manualValues?: Record<string, string>;
  /**
   * Values typed for profile fields that are empty for this person (or
   * that the sender may not read), by token (`employee.address`).
   */
  fieldValues?: Record<string, string>;
  /** The body edited for this person only, placeholders kept. */
  bodyHtml?: string;
  companySignatoryAdminId?: string;
  /** An employee id or `direct_manager`; wins over the admin id and default. */
  companySignatory?: string;
  requireApproval?: boolean;
  approverAdminId?: string;
  /** Adds a step before the employee to a template without one. */
  beforeEmployee?: BeforeEmployeeStep;
  /** Who approves: a company admin id or `direct_manager`. */
  approver?: string;
  /** Tell the employee's direct manager (defaults to the template's). */
  notifyManager?: boolean;
  /**
   * Send without the approval the template requires. The server accepts it
   * only when no other admin may approve, and notes it in the history.
   */
  skipApproval?: boolean;
  action: SignatureComposeAction;
  /** The onboarding / offboarding step this is sent for (D68). */
  flowTaskId?: string;
}

export interface SignatureRequestSigner {
  id: string;
  role: SignatureSignerRole;
  signerType: 'employee' | 'candidate' | 'company_admin' | 'external';
  signingOrder: number;
  name: string;
  email: string;
  status: SignatureSignerStatus;
  employeeId: string | null;
  companyAdminId: string | null;
  notifiedAt: string | null;
  viewedAt: string | null;
  signedAt: string | null;
  declinedAt: string | null;
  declineReason: string | null;
  otpVerifiedAt: string | null;
}

/** An ATS offer's candidate (no employee yet). */
export interface SignatureRequestCandidate {
  applicationId: string;
  name: string;
}

/** What an offer holds besides its document (spec 7.5). */
export interface OfferTerms {
  startDate: string;
  /** Only for salary readers or the sender. */
  salary: { amount: number | string; currency: string; payType: string } | null;
}

export interface SignatureRequestListItem {
  id: string;
  title: string;
  status: SignatureRequestStatus;
  language: DocumentLanguage;
  employee: { id: string; name: string } | null;
  candidate: SignatureRequestCandidate | null;
  sentBy: string;
  sentAt: string | null;
  expiresAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  filed: boolean;
  waitingOn: SignatureWaitingOn;
  /** A draft the approver sent back with a comment: it waits on the sender. */
  changesRequested?: boolean;
  signers: SignatureRequestSigner[];
}

export interface SignatureRequestList {
  items: SignatureRequestListItem[];
  total: number;
  page: number;
  limit: number;
}

export interface SignatureRequestListQuery {
  page?: number;
  limit?: number;
  /** Comma-separated statuses. */
  statuses?: string;
  employeeId?: string;
  applicationId?: string;
  waitingOnMe?: 'approval' | 'signature';
}

export interface SignatureRequestEvent {
  id: string;
  type: string;
  at: string;
  actor: string | null;
  signer: string | null;
  ip: string | null;
  metadata: Record<string, string | number | boolean | null>;
}

export interface SignatureRequestDetail {
  id: string;
  title: string;
  status: SignatureRequestStatus;
  language: DocumentLanguage;
  employee: { id: string; name: string } | null;
  candidate: SignatureRequestCandidate | null;
  offerTerms: OfferTerms | null;
  templateId: string | null;
  templateVersion: number | null;
  /** Phase 4: built from an uploaded PDF (no preview; the PDF is the file). */
  uploaded?: boolean;
  /**
   * The uploaded PDF's file name. A revision keeps the PDF unless a new one
   * is uploaded (omit `uploadedPdfKey` to keep it).
   */
  uploadedPdfName?: string | null;
  /** The broadcast for signature this request is one copy of. */
  broadcastId?: string | null;
  editedFromTemplate: boolean;
  /** Drafts only, for whoever may edit them. */
  bodyHtml: string | null;
  /**
   * The manual fields' values by key, plus values typed for empty profile
   * fields by token (keys with a dot, `employee.address`). Drafts only.
   */
  manualValues: Record<string, string> | null;
  salarySource: 'profile' | 'manual' | null;
  /**
   * The salary typed for this document, to revise a draft. Drafts only, for
   * the sender or readers with salary access.
   */
  manualSalary?: {
    amount: number | string;
    currency: string;
    payType: string;
  } | null;
  usesSalary: boolean;
  /** Merged for reading, any salary masked. */
  previewHtml: string | null;
  contentSha256: string | null;
  sequential: boolean;
  approver: {
    companyAdminId: string | null;
    employeeId?: string | null;
    name: string;
    /** Resolved from the employee's direct manager at send. */
    viaDirectManager?: boolean;
    /** The viewer is the approver. */
    isMe?: boolean;
    /** No longer active: HR may decide in their place. */
    inactive?: boolean;
  } | null;
  /** Tell the employee's direct manager when sent and when signed. */
  notifyManager?: boolean;
  approvalComment: string | null;
  changesRequestedAt: string | null;
  approvedAt: string | null;
  approvedBy: string | null;
  sentBy: string | null;
  createdAt: string;
  sentAt: string | null;
  lastViewedAt: string | null;
  completedAt: string | null;
  declinedAt: string | null;
  declineReason: string | null;
  expiresAt: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  lastRemindedAt: string | null;
  reminderCount: number;
  failureReason: string | null;
  resentFromRequestId: string | null;
  employeeDocumentId: string | null;
  waitingOn: SignatureWaitingOn;
  signers: SignatureRequestSigner[];
  events: SignatureRequestEvent[];
  files: {
    source: boolean;
    signed: boolean;
    certificate: boolean;
    auditLog: boolean;
    evidence: boolean;
  };
  permissions: {
    canApprove: boolean;
    canRequestChanges: boolean;
    canEdit: boolean;
    canCancel: boolean;
    canRemind: boolean;
    canSendAgain: boolean;
    canRetry: boolean;
    canSignNow: boolean;
    canSeeSalary: boolean;
  };
  /** The recipient fills fields in before signing; not done yet. */
  awaitingAnswers?: boolean;
  /** The recipient's fields and answers (null values until answered). */
  answers?: RecipientAnswerView[] | null;
  /** What each field saved to the employee file did (2026-10-10). */
  profileUpdates?: ProfileUpdateView[];
}

/** An employee in the company signatory picker. */
export interface SignatoryEmployeeOption {
  employeeId: string;
  name: string;
  jobTitle: string | null;
  /** False: no user account with an email yet, so they cannot sign. */
  canSign: boolean;
}

export interface SignatureAdminOption {
  companyAdminId: string;
  userId: string;
  name: string;
  email: string | null;
}

export interface SignatureRequestPeople {
  approvers: SignatureAdminOption[];
  signatories: SignatureAdminOption[];
  /** Everyone active who may be picked to sign for the company. */
  signatoryEmployees?: SignatoryEmployeeOption[];
  /** False while the signing engine is not configured (503 on send). */
  enabled: boolean;
}

/** Employee portal: where one of my requests sits. */
export type MySignatureBucket =
  'to_sign' | 'in_progress' | 'completed' | 'closed';

export interface MySignatureRequest {
  id: string;
  title: string;
  status: SignatureRequestStatus;
  myStatus: SignatureSignerStatus;
  sentAt: string | null;
  expiresAt: string | null;
  completedAt: string | null;
  signedAt: string | null;
  sentBy: string;
  filed: boolean;
  /**
   * Sent by an onboarding / offboarding step. My Tasks lists the document in
   * place of that step's own row (`flowTaskId` is the step's task id).
   */
  fromFlowStep?: boolean;
  flowTaskId?: string | null;
  bucket: MySignatureBucket;
}

export interface MySignatureRequestDetail extends MySignatureRequest {
  language: DocumentLanguage;
  declineReason: string | null;
  cancelReason: string | null;
  files: { signed: boolean; certificate: boolean; evidence: boolean };
}

/** What the public signing gate shows for an emailed link (D74). */
export type SignatureGateStep =
  | 'login'
  | 'code'
  | 'waiting'
  | 'signed'
  | 'declined'
  | 'expired'
  | 'withdrawn';

export interface SignatureGateView {
  step: SignatureGateStep;
  title: string;
  companyName: string;
  portal?: 'employee' | 'company';
  requestId?: string;
  maskedEmail?: string;
  codeSent?: boolean;
  language: DocumentLanguage;
}

export interface SignatureUrl {
  url: string;
}

/** The offer a candidate's profile shows (ATS, spec 7.4). */
export interface ApplicationOffer {
  enabled: boolean;
  /** esignature.create and recruiting.update. */
  canSend: boolean;
  defaultTemplateId: string | null;
  offer: {
    id: string;
    title: string;
    status: SignatureRequestStatus;
    language: DocumentLanguage;
    createdAt: string;
    sentAt: string | null;
    lastViewedAt: string | null;
    completedAt: string | null;
    declinedAt: string | null;
    declineReason: string | null;
    expiresAt: string | null;
    cancelledAt: string | null;
    waitingOn: SignatureWaitingOn;
    /** The viewer is the company signatory and it is their turn. */
    canSignNow?: boolean;
    filed: boolean;
    copiedToEmployee: boolean;
    terms: OfferTerms | null;
  } | null;
  count: number;
}

export type GetApplicationOfferResponse = ApiResponse<ApplicationOffer>;

export type GetSignatureRequestsResponse = ApiResponse<SignatureRequestList>;
export type GetSignatureRequestResponse = ApiResponse<SignatureRequestDetail>;
export type GetSignatureRequestPeopleResponse =
  ApiResponse<SignatureRequestPeople>;
export type GetSignatureUrlResponse = ApiResponse<SignatureUrl>;
export type GetMySignatureRequestsResponse = ApiResponse<MySignatureRequest[]>;
export type GetMySignatureRequestResponse =
  ApiResponse<MySignatureRequestDetail>;
export type GetSignatureGateResponse = ApiResponse<SignatureGateView>;

/** The viewer's calendar day (YYYY-MM-DD) of an instant. */
const localDay = (iso: string) => {
  const at = new Date(iso);
  const month = String(at.getMonth() + 1).padStart(2, '0');
  const day = String(at.getDate()).padStart(2, '0');
  return `${at.getFullYear()}-${month}-${day}`;
};

/** A document waiting on the employee, listed in their Tasks. */
export interface PendingDocumentTask {
  kind: 'signature' | 'acknowledgement';
  id: string;
  title: string;
  /** The day it is due (YYYY-MM-DD), or null. */
  dueDay: string | null;
  overdue: boolean;
  /**
   * The onboarding / offboarding step that sent it, or null. That step's own
   * task row is not listed beside it (`withoutDocumentSteps`).
   */
  flowTaskId: string | null;
}

/**
 * The employee's Tasks also list the documents still waiting on them: the
 * ones to sign and the ones to read and confirm, soonest due first, whether
 * HR sent them or a workflow step did, so the list matches the "Documents to
 * sign and read" badge. One signed or read drops out with its status.
 * `today` is YYYY-MM-DD.
 */
export function pendingDocumentTasks(
  signatures: MySignatureRequest[],
  acknowledgements: MyAcknowledgement[],
  today: string
): PendingDocumentTask[] {
  const items: PendingDocumentTask[] = [
    ...signatures
      .filter((item) => item.bucket === 'to_sign')
      .map((item) => ({
        kind: 'signature' as const,
        id: item.id,
        title: item.title,
        dueDay: item.expiresAt ? localDay(item.expiresAt) : null,
        flowTaskId: item.flowTaskId ?? null,
      })),
    ...acknowledgements
      .filter((item) => item.status === AcknowledgementStatus.Pending)
      .map((item) => ({
        kind: 'acknowledgement' as const,
        id: item.id,
        title: item.title,
        dueDay: item.dueDate,
        flowTaskId: item.flowTaskId ?? null,
      })),
  ].map((item) => ({ ...item, overdue: !!item.dueDay && item.dueDay < today }));
  return items.sort((a, b) =>
    a.dueDay === b.dueDay
      ? a.title.localeCompare(b.title)
      : !a.dueDay
        ? 1
        : !b.dueDay
          ? -1
          : a.dueDay.localeCompare(b.dueDay)
  );
}

/**
 * The task rows My Tasks still shows once `documents` are listed: a workflow
 * step whose document is listed is that document, so its row is dropped
 * rather than shown twice. Every other row stays, a step whose document is
 * not out yet (HR reviews it first) or is done included.
 */
export function withoutDocumentSteps<T extends { taskId: string }>(
  tasks: T[],
  documents: Pick<PendingDocumentTask, 'flowTaskId'>[]
): T[] {
  const listed = new Set(
    documents.flatMap((item) => (item.flowTaskId ? [item.flowTaskId] : []))
  );
  return listed.size ? tasks.filter((task) => !listed.has(task.taskId)) : tasks;
}

/** The fields the recipient fills in before signing (employee portal). */
export interface MySignatureAnswersForm {
  id: string;
  title: string;
  language: string;
  awaitingAnswers: boolean;
  fields: RecipientFieldView[];
  values: RecipientAnswers;
}
export type GetMySignatureAnswersFormResponse =
  ApiResponse<MySignatureAnswersForm>;

/** Employee portal: a document waiting for my approval, or one I decided. */
export interface MySignatureApproval {
  id: string;
  title: string;
  status: SignatureRequestStatus;
  employeeName: string;
  sentBy: string;
  approvalRequestedAt: string | null;
  approvedAt: string | null;
  changesRequestedAt: string | null;
  /** Still waiting for me. */
  waiting: boolean;
}

export interface MySignatureApprovalDetail extends MySignatureApproval {
  language: DocumentLanguage;
  approvalComment: string | null;
  uploaded: boolean;
  /** Merged for reading: salary and personal details masked. */
  previewHtml: string | null;
  signers: { role: SignatureSignerRole; name: string; signingOrder: number }[];
  canDecide: boolean;
}

export type GetMySignatureApprovalsResponse =
  ApiResponse<MySignatureApproval[]>;
export type GetMySignatureApprovalResponse =
  ApiResponse<MySignatureApprovalDetail>;
